// Contrail V3.11.0 — Admin → Status (Super Admins) and the break-glass page /status-admin (status key).
// Post a notice users see on every app page and/or the sign-in page: planned maintenance, upgrades, service issues.
// Saved through /api/status in Netlify Blobs — independent of Supabase.
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Field, Notice as Msg, Toolbar, fmtDateTime } from "./ui";
import {
  LEVEL_LABEL, MAX_MESSAGE, MAX_TITLE, NOTICE_LEVELS, NOTICE_TEMPLATES, adminState, checkNoticeInput, fijiLocalToIso,
  isoToFijiLocal, type Notice, type NoticeLevel,
} from "../../lib/statusNotice";
import { refreshStatus } from "../../lib/useStatusNotices";
import { NoticePreview } from "../../ui/StatusNotice";

export type StatusApi = <T = any>(method: string, path: string, body?: unknown) => Promise<T>;

/** Calls /api/status with either the signed-in user's token or the break-glass key. */
export function statusApi(auth: { token?: () => Promise<string | null>; key?: string }): StatusApi {
  return async (method, path, body) => {
    const headers: Record<string, string> = { Accept: "application/json" };
    if (auth.key) headers["x-status-key"] = auth.key;
    else {
      const t = await auth.token?.();
      if (!t) throw new Error("Please sign in again.");
      headers.Authorization = `Bearer ${t}`;
    }
    if (body !== undefined) headers["Content-Type"] = "application/json";
    const r = await fetch(`/api/status${path}`, { method, headers, body: body !== undefined ? JSON.stringify(body) : undefined, cache: "no-store" });
    const res = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(res?.error ?? `Request failed (${r.status})`);
    return res;
  };
}

type Form = {
  level: NoticeLevel; title: string; message: string;
  timing: "now" | "window"; start: string; end: string;       // datetime-local, Fiji time
  announce: "now" | "1" | "6" | "24" | "48";                  // hours before the start (window only)
  show_app: boolean; show_login: boolean;
};
const EMPTY: Form = { level: "upgrade", title: "", message: "", timing: "window", start: "", end: "", announce: "24", show_app: true, show_login: true };

function toBody(f: Form) {
  const starts_at = f.timing === "window" ? fijiLocalToIso(f.start) : null;
  const ends_at = f.timing === "window" ? fijiLocalToIso(f.end) : null;
  const announce_from = f.timing === "window" && starts_at && f.announce !== "now"
    ? new Date(Math.max(Date.now(), Date.parse(starts_at) - Number(f.announce) * 3600_000)).toISOString() : null;
  return { level: f.level, title: f.title, message: f.message, starts_at, ends_at, announce_from, show_app: f.show_app, show_login: f.show_login };
}
function fromNotice(n: Notice): Form {
  const hrs = n.announce_from && n.starts_at ? Math.round((Date.parse(n.starts_at) - Date.parse(n.announce_from)) / 3600_000) : 0;
  const announce = (["1", "6", "24", "48"].find((h) => Number(h) === hrs) ?? "now") as Form["announce"];
  return { level: n.level, title: n.title, message: n.message, timing: n.starts_at || n.ends_at ? "window" : "now",
    start: isoToFijiLocal(n.starts_at), end: isoToFijiLocal(n.ends_at), announce, show_app: n.show_app, show_login: n.show_login };
}

const STATE_PILL = { live: ["ok", "Showing"], scheduled: ["info", "Scheduled"], ended: ["plain", "Ended"] } as const;

export default function StatusAdmin({ api, breakGlass = false }: { api: StatusApi; breakGlass?: boolean }) {
  const [list, setList] = useState<Notice[] | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [f, setF] = useState<Form>(EMPTY);
  const [editing, setEditing] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "bad"; text: string } | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);

  const load = useCallback(async () => {
    try { const r = await api<{ notices: Notice[] }>("GET", "?all=1"); setList(r.notices); setLoadErr(null); }
    catch (e: any) { setLoadErr(e.message); }
  }, [api]);
  useEffect(() => { void load(); }, [load]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setF((x) => ({ ...x, [k]: v }));
  const body = toBody(f);
  const problem = useMemo(() => {
    if (!f.title.trim() && !f.message.trim()) return null;
    if (f.timing === "window" && !f.end) return "Set when it ends (it hides itself after that)";
    try { checkNoticeInput(body, { editing: !!editing }); return null; } catch (e: any) { return e.message as string; }
  }, [body, f, editing]);
  const preview = { id: "preview", ...body, updated_at: "" };

  async function save(e: FormEvent) {
    e.preventDefault();
    if (problem) return;
    setBusy(true); setMsg(null);
    try {
      if (editing) await api("PATCH", `/${editing}`, body); else await api("POST", "", body);
      setMsg({ kind: "ok", text: editing ? "Notice updated. Users see the change within about a minute." : "Notice posted. Users see it within about a minute." });
      setF(EMPTY); setEditing(null);
      await load(); void refreshStatus();
    } catch (x: any) { setMsg({ kind: "bad", text: x.message }); }
    finally { setBusy(false); }
  }
  async function act(n: Notice, what: "end" | "delete") {
    setBusy(true); setMsg(null);
    try {
      if (what === "end") await api("POST", `/${n.id}/end`, {}); else await api("DELETE", `/${n.id}`);
      setMsg({ kind: "ok", text: what === "end" ? `"${n.title}" ended — it disappears for users within about a minute.` : `"${n.title}" deleted.` });
      if (editing === n.id) { setEditing(null); setF(EMPTY); }
      setConfirmDel(null);
      await load(); void refreshStatus();
    } catch (x: any) { setMsg({ kind: "bad", text: x.message }); }
    finally { setBusy(false); }
  }

  return (
    <section className="status-admin">
      <Toolbar>
        <h1>{breakGlass ? "Platform notices (status key)" : "Status"}</h1>
        <span className="sub">Tell users about planned maintenance, upgrades or problems. Works even when the database is down.</span>
      </Toolbar>
      {msg && <Msg kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Msg>}

      <div className="st-grid">
        <form className="st-card" onSubmit={save}>
          <h2>{editing ? "Edit notice" : "New notice"}</h2>
          {!editing && (
            <div className="st-templates">
              <span>Start from:</span>
              {NOTICE_TEMPLATES.map((t) => (
                <button type="button" key={t.label} className="btn-ghost small" onClick={() => setF((x) => ({ ...x, level: t.level, title: t.title, message: t.message }))}>{t.label}</button>
              ))}
            </div>
          )}
          <Field label="Type">
            <select value={f.level} onChange={(e) => set("level", e.target.value as NoticeLevel)}>
              {NOTICE_LEVELS.map((l) => <option key={l} value={l}>{LEVEL_LABEL[l]}</option>)}
            </select>
          </Field>
          <Field label="Title" hint={`${f.title.length}/${MAX_TITLE}`}>
            <input value={f.title} maxLength={MAX_TITLE} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Platform upgrades in progress" />
          </Field>
          <Field label="Message" hint={`${f.message.length}/${MAX_MESSAGE} · plain words, one or two sentences`}>
            <textarea rows={3} value={f.message} maxLength={MAX_MESSAGE} onChange={(e) => set("message", e.target.value)} />
          </Field>
          <div className="st-row">
            <label className="st-radio"><input type="radio" checked={f.timing === "window"} onChange={() => set("timing", "window")} /> Set a time window</label>
            <label className="st-radio"><input type="radio" checked={f.timing === "now"} onChange={() => set("timing", "now")} /> From now until I end it</label>
          </div>
          {f.timing === "window" && (
            <>
              <div className="st-row two">
                <Field label="Starts (Fiji time)" hint="Leave empty if it has already started"><input type="datetime-local" value={f.start} onChange={(e) => set("start", e.target.value)} /></Field>
                <Field label="Ends (Fiji time)" hint="Hidden automatically after this"><input type="datetime-local" value={f.end} onChange={(e) => set("end", e.target.value)} /></Field>
              </div>
              {f.start && (
                <Field label="Start showing it">
                  <select value={f.announce} onChange={(e) => set("announce", e.target.value as Form["announce"])}>
                    <option value="now">Straight away</option>
                    <option value="1">1 hour before it starts</option>
                    <option value="6">6 hours before</option>
                    <option value="24">24 hours before</option>
                    <option value="48">2 days before</option>
                  </select>
                </Field>
              )}
            </>
          )}
          <div className="st-row">
            <label className="st-check"><input type="checkbox" checked={f.show_app} onChange={(e) => set("show_app", e.target.checked)} /> Every app page (desktop + phone)</label>
            <label className="st-check"><input type="checkbox" checked={f.show_login} onChange={(e) => set("show_login", e.target.checked)} /> Sign-in page</label>
          </div>
          {(f.title || f.message) && (
            <div className="st-preview">
              <span className="st-prev-lbl">Preview</span>
              <NoticePreview n={preview} />
            </div>
          )}
          {problem && <div className="error" role="alert">{problem}</div>}
          <div className="st-actions">
            {editing && <button type="button" className="btn-ghost" onClick={() => { setEditing(null); setF(EMPTY); }}>Cancel</button>}
            <button className="btn-primary" disabled={busy || !!problem || !f.title.trim() || !f.message.trim()}>{busy ? "Saving…" : editing ? "Save changes" : "Post notice"}</button>
          </div>
        </form>

        <div className="st-card">
          <h2>Notices</h2>
          {loadErr && <div className="error">{loadErr}</div>}
          {!list && !loadErr && <p className="muted">Loading…</p>}
          {list && !list.length && <p className="muted">No notices yet.</p>}
          <ul className="st-list">
            {(list ?? []).map((n) => {
              const s = adminState(n);
              return (
                <li key={n.id} className={`st-item st-${s}`}>
                  <div className="st-item-head">
                    <span className={`apill ${STATE_PILL[s][0]}`}>{STATE_PILL[s][1]}</span>
                    <span className="apill plain">{LEVEL_LABEL[n.level]}</span>
                    <b>{n.title}</b>
                  </div>
                  <p>{n.message}</p>
                  <div className="st-meta">
                    {n.starts_at && <span>Starts {fmtDateTime(n.starts_at)}</span>}
                    <span>{n.ends_at ? `Ends ${fmtDateTime(n.ends_at)}` : "Until ended"}</span>
                    {n.announce_from && s === "scheduled" && <span>Shows from {fmtDateTime(n.announce_from)}</span>}
                    <span>{[n.show_app && "App", n.show_login && "Sign-in"].filter(Boolean).join(" + ")}</span>
                    <span>By {n.updated_by} · {fmtDateTime(n.updated_at)}{n.ended_at ? ` · ended ${fmtDateTime(n.ended_at)}` : ""}</span>
                  </div>
                  <div className="st-item-actions">
                    {s !== "ended" && <button type="button" className="btn-ghost small" disabled={busy} onClick={() => { setEditing(n.id); setF(fromNotice(n)); setMsg(null); }}>Edit</button>}
                    {s !== "ended" && <button type="button" className="btn-ghost small" disabled={busy} onClick={() => act(n, "end")}>End now</button>}
                    {confirmDel === n.id
                      ? <button type="button" className="btn-ghost small st-danger" disabled={busy} onClick={() => act(n, "delete")}>Confirm delete</button>
                      : <button type="button" className="btn-ghost small" disabled={busy} onClick={() => setConfirmDel(n.id)}>Delete</button>}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
